/**
 * Builds a mailto: link to support with a diagnostics block appended to the body.
 *
 * Shared by the two error surfaces so both send support the same block:
 *  - GlobalErrorView (error boundary) passes `error` / `errorInfo`.
 *  - NotFound (404) has no error object and passes `details` instead.
 */

const SUPPORT_ADDRESS = "support@ascent360.com";

function resolveUser(userInfo) {
  return (
    userInfo?.userName ||
    localStorage.getItem("user.userName") ||
    "Unknown"
  );
}

function resolveClientId(clientId) {
  return (
    clientId ||
    localStorage.getItem("selectedClientId") ||
    "Unknown"
  );
}

export function buildSupportMailto({
  userInfo,
  clientId,
  error,
  errorInfo,
  // Emit the "=== Error ===" block. Explicit rather than inferred from
  // `error`/`errorInfo` so the boundary keeps its placeholder lines even when
  // it renders without props (the /test-error debug route does exactly that).
  includeError = false,
  // Drop the query string from the URL line. Callers that can be reached with
  // contact identifiers in the query opt in; the boundary keeps the full URL
  // because a real stack trace is usually unreadable without it.
  sanitizeUrl = false,
  subjectDetail,
  details,
} = {}) {
  const username = resolveUser(userInfo);
  const resolvedClientId = resolveClientId(clientId);

  const url = sanitizeUrl
    ? `${window.location.origin}${window.location.pathname}`
    : window.location.href;

  const lines = [
    // Open with blank space so the compose window gives the user somewhere to
    // type. Without it they land on a wall of diagnostics and have to scroll
    // past it to say what they were doing, which is the part support needs.
    "",
    "",
    "--- Details below are filled in automatically. Please leave them in. ---",
    "",
    "=== Environment ===",
    `Username: ${username}`,
    `ClientId: ${resolvedClientId}`,
    `URL: ${url}`,
    `Timestamp: ${new Date().toString()}`,
    `Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    `User Agent: ${navigator.userAgent}`,
    `Platform: ${navigator.platform}`,
    `Viewport: ${window.innerWidth} x ${window.innerHeight}`,
    `Device Pixel Ratio: ${window.devicePixelRatio}`,
  ];

  if (details) {
    lines.push("", "=== Details ===", details);
  }

  if (includeError) {
    lines.push(
      "",
      "=== Error ===",
      `Error Message: ${error?.message || "No error message available"}`,
      "",
      "Stack Trace:",
      error?.stack || "No stack trace available",
      "",
      "Component Stack:",
      errorInfo?.componentStack || "No component stack available"
    );
  }

  const subject = `User ${username} ${subjectDetail || "encountered an error"}`;

  return (
    `mailto:${SUPPORT_ADDRESS}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(lines.join("\n"))}`
  );
}
